import 'chartjs-adapter-date-fns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentClientCurrency } from 'hooks/useCurrentClientCurrency';

import TooltipPopover from 'components/TooltipPopover';

// COMPONENTS
import LoadingSpinner from './LoadingSpinner';
import {
  Chart,
  Title,
  Legend,
  Tooltip,
  ChartData,
  TimeScale,
  LineElement,
  LinearScale,
  PointElement,
  CategoryScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { noDataPlugin } from 'utils';

Chart.register(
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Info {
  transactions: string;
  revenue: string;
}

interface InsightsHomeProps {
  insights_data?: InsightsHomeData;
  error?: unknown;
  isLoading?: boolean;
}

function Insights({ insights_data, error, isLoading }: InsightsHomeProps) {
  const [insight_blur, setBlur] = useState(false);
  const { replaceSymbolIfCurrencyNotUSD } = useCurrentClientCurrency(); // function from custom hook to change symbol if client curr not USD

  useEffect(() => {
    if (!insights_data) return;
    setBlur(insights_data.insightsBlur);
  }, [insights_data]);

  const navigate = useNavigate();
  const handleRedirect = () => {
    navigate('/insights');
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>Error fetching insights data: {(error as Error).message}</div>;
  }

  if (!insights_data || insights_data.data.length === 0) {
    return <div></div>;
  }

  const labels = insights_data.data.map(
    (item: { label: string; value: number }) => item.label
  );
  const values = insights_data.data.map(
    (item: { label: string; value: number }) => item.value
  );

  const chartData: ChartData<'line'> = {
    labels: labels,
    datasets: [
      {
        label: 'Transactions',
        data: values,
        borderColor: '#222222CC',
        backgroundColor: 'rgba(34, 34, 34, 0.80)',
        fill: false,
        pointRadius: 2,
        pointBackgroundColor: '#FFFFFF',
        pointBorderWidth: 1,
        borderWidth: 1,
        tension: 0.0,
      },
    ],
  };

  const info: Info = {
    transactions: insights_data.details.transactions.title,
    revenue: replaceSymbolIfCurrencyNotUSD(insights_data.details.revenue.title), // function (replaceSymbol...) replaces currency symbol as per applicable currency
  };

  const formatNumber = (num: number): string => {
    const thresholds = [
      { value: 1e9, suffix: 'B' },
      { value: 1e6, suffix: 'M' },
      { value: 1e3, suffix: 'K' },
    ];

    for (const threshold of thresholds) {
      if (num >= threshold.value) {
        return (num / threshold.value).toFixed(1) + threshold.suffix;
      }
    }

    return num.toString();
  };

  return (
    <div className="bg-light rounded-lg shadow-md h-[413px] w-full lg:h-auto">
      {insight_blur && (
        <span className="block bg-light text-primarygray p-4 mb-4 rounded">
          NO DATA FOR THIS PRODUCT
        </span>
      )}
      <div className={`relative ${insight_blur ? 'filter blur-sm' : ''}`}>
        <div className="px-4 py-2 flex justify-between items-center border-b border-success">
          <div>
            <TooltipPopover
              content="homepage.insights.insights"
              type="info"
              className="top-1 right-[11.1rem]"
              side="top"
              tooltipWidth="max-w-72"
            >
              <h3
                className="text-primarygray text-2xl font-bold cursor-pointer tracking-[0.48px]"
                onClick={handleRedirect}
              >
                Insights
              </h3>
            </TooltipPopover>
            <h6 className="text-sm text-gray400 font-semibold flex items-center">
              Holistic search data and recommendations
            </h6>
          </div>
          <h6 className="text-sm font-semibold text-gray400 mt-8">
            Past 30 days
          </h6>
        </div>
        <div className="flex">
          <div className="py-6 px-4 flex-1 min-w-[200px] flex flex-col justify-center">
            <div className="mb-4">
              <TooltipPopover
                content="homepage.insights.holistic_transaction"
                type="dashed"
                side="top"
                className="underline decoration-dashed decoration-1 w-fit"
              >
                <h6 className="text-sm font-medium text-thirdgray w-fit">
                  Holistic Transactions
                </h6>
              </TooltipPopover>
              <div className="flex items-center mt-2">
                <h3 className="text-primarygray text-3xl font-medium">
                  {info.transactions}
                </h3>
              </div>
            </div>
            <div>
              <TooltipPopover
                content="homepage.insights.holistic_revenue"
                type="dashed"
                side="top"
                className="underline decoration-dashed decoration-1 w-fit"
              >
                <h6 className="text-sm font-medium text-thirdgray w-fit">
                  Holistic Revenue
                </h6>
              </TooltipPopover>
              <div className="flex items-center mt-2">
                <h3 className="text-primarygray text-3xl font-medium">
                  {info.revenue}
                </h3>
              </div>
            </div>
          </div>
          <div className="flex-1 py-6 px-4">
            {isLoading ? (
              <p className="text-thirdgray">Loading data...</p>
            ) : (
              <div className="w-[307px] h-[251px] 2xl:w-[370px] 3xl:w-[500px]">
                <Line
                  data={chartData}
                  options={{
                    scales: {
                      x: {
                        type: 'time',
                        time: {
                          unit: 'day',
                          tooltipFormat: 'MM/dd',
                          displayFormats: {
                            day: 'MM/dd',
                          },
                        },
                        ticks: {
                          display: true,
                          color: '#333333',
                          maxRotation: 0,
                          minRotation: 0,
                          autoSkip: true,
                          maxTicksLimit: Math.ceil(
                            (chartData.labels as string[]).length / 10
                          ),
                          // callback: function(value, index) {
                          //     const label = (insights_data.labels as string[])[index];
                          //     return format(new Date(label), 'dd/MM');
                          // },
                          font: {
                            size: 12, // Set font size
                            style: 'normal', // Set font style
                            weight: 400, // Set font weight
                            lineHeight: 1.33, // Set line height (16px / 12px = 1.33)
                          },
                        },
                        border: {
                          color: '#333333',
                          display: true,
                        },
                        grid: {
                          color: '#B3B3B3', // Display vertical grid lines
                        },
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Transactions',
                          font: {
                            size: 14, // Set font size
                            style: 'normal', // Set font style
                            weight: 700, // Set font weight
                            lineHeight: 1.28, // Set line height (18px / 14px = 1.28)
                          },
                          align: 'center',
                        },
                        ticks: {
                          display: true,
                          color: '#000',
                          callback: function (value) {
                            const numericValue = Number(value);
                            return formatNumber(numericValue);
                          },
                          font: {
                            size: 12, // Set font size
                            style: 'normal', // Set font style
                            weight: 400, // Set font weight
                            lineHeight: 1.33, // Set line height (16px / 12px = 1.33)
                          },
                          align: 'center',
                        },
                        border: {
                          color: '#333333',
                          display: true,
                        },
                        grid: {
                          color: '#B3B3B3', // Display vertical grid lines
                        },
                      },
                    },
                    plugins: {
                      legend: { display: false },
                    },
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    maintainAspectRatio: false,
                    responsive: true,
                  }}
                  plugins={[noDataPlugin]}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex px-4 py-2 justify-end">
          <a
            className="inline-flex items-center w-[110px] h-[30px] justify-center text-thirdgray text-sm font-normal bg-fourthgray rounded-[8px] cursor-pointer"
            onClick={handleRedirect}
          >
            See more
            <img
              src={`${process.env.PUBLIC_URL}/static/img/right-arrow.svg`}
              className="w-4 h-4 bg-contain bg-center ml-1"
              alt="right-arrow"
            />
          </a>
        </div>
      </div>
    </div>
  );
}

export default Insights;
